import React, { useState } from 'react';
import ScenarioDetailsModal from './ScenarioDetailsModal';
import CostesExtraModal from './CostesExtraModal';
import { 
    sortScenariosAndConstructors, 
    normalizeScenarioName, 
    getScenarioIcon, 
    findBestOverall, 
    processScenariosData,
    formatCurrency,
    getScenarioData
} from '../utils/investmentIndicatorsHelper';

const AdvancedInvestmentIndicators = ({ indicadores, indicadoresOriginales, vacacional, loadingVacacional, onCalcularVacacional, onShowDetails, costesVacacional, onCargarCostesVacacional, modoIA = false, preserveOrder = false }) => {
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [showCostesExtraModal, setShowCostesExtraModal] = useState(false);

    const handleShowDetails = (scenario, scenarioName, constructor) => {
        setSelectedScenario({ ...scenario, scenarioName, constructor });
        setShowDetailsModal(true);
    };

    const closeModal = () => {
        setShowDetailsModal(false);
        setSelectedScenario(null);
    };

    const renderGroup = (title, items, isVenta = true, color = "blue") => {
        if (!items || items.length === 0) return null;

        if (preserveOrder) {
            // Group by scenario name in order of appearance
            const scenariosByName = {};
            const scenarioOrder = [];
            const constructorOrder = [];

            items.forEach(item => {
                const scenarioName = item.tipoReforma.nombre;
                const constructor = item.constructor || 'Sin constructor';
                if (!scenariosByName[scenarioName]) {
                    scenariosByName[scenarioName] = [];
                    scenarioOrder.push(scenarioName);
                }
                if (!constructorOrder.includes(constructor)) {
                    constructorOrder.push(constructor);
                }
                scenariosByName[scenarioName].push(item);
            });

            return (
                <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4"><span className={`inline-block w-2 h-2 rounded-full bg-${color}-500 mr-2`}></span>{title}</h3>
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        {/* Header */}
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                            <div className="grid gap-4" style={{ 
                                gridTemplateColumns: `200px repeat(${constructorOrder.length}, 1fr)`,
                                gridAutoFlow: 'row dense'
                            }}>
                                <div className="text-xs text-gray-500 uppercase tracking-wide font-medium" style={{ order: 0 }}>Escenario</div>
                                {constructorOrder.map((constructor, colIndex) => (
                                    <div 
                                        key={`header-${constructor}-${colIndex}`} 
                                        className="text-xs text-gray-500 uppercase tracking-wide font-medium text-center"
                                        style={{ order: colIndex + 1 }}
                                    >
                                        {constructor}
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Rows */}
                        {scenarioOrder.map((scenarioName, rowIndex) => {
                            const scenarioItems = scenariosByName[scenarioName];
                            return (
                                <div 
                                    key={`row-${scenarioName}-${rowIndex}`} 
                                    className="border-b border-gray-100"
                                    style={{ order: rowIndex }}
                                >
                                    <div className="grid gap-4 px-6 py-4" style={{ 
                                        gridTemplateColumns: `200px repeat(${constructorOrder.length}, 1fr)`,
                                        gridAutoFlow: 'row dense'
                                    }}>
                                        {/* Scenario name */}
                                        <div className="flex items-center gap-2" style={{ order: 0 }}>
                                            <span className="text-2xl">{getScenarioIcon(scenarioName)}</span>
                                            <div className="font-semibold text-gray-900">{scenarioName}</div>
                                        </div>
                                        {/* Constructor columns */}
                                        {constructorOrder.map((constructor, colIndex) => {
                                            const item = scenarioItems.find(si => (si.constructor || 'Sin constructor') === constructor);
                                            if (!item) {
                                                return (
                                                    <div 
                                                        key={`cell-${scenarioName}-${constructor}-${rowIndex}-${colIndex}`} 
                                                        className="text-center text-gray-400"
                                                        style={{ order: colIndex + 1 }}
                                                    >
                                                        -
                                                    </div>
                                                );
                                            }
                                            // Card rendering logic (unchanged)
                                            const { hasIA, costeActual, mainValue, iaMainValue, subValue, iaSubValue } = getScenarioData(item, isVenta);
                                            let costeOriginal, rentabilidadOriginal, subValueOriginal;
                                            if (modoIA && indicadoresOriginales) {
                                                if (item.originalData) {
                                                    costeOriginal = item.originalData.costeReforma;
                                                    rentabilidadOriginal = isVenta ? item.originalData.venta?.rentabilidadAnual : item.originalData.alquiler?.rentabilidadAnual;
                                                    subValueOriginal = isVenta ? null : (item.originalData.alquiler?.tiempoRetornoMeses ? `${item.originalData.alquiler?.tiempoRetornoMeses} meses` : null);
                                                } else {
                                                    const originalItem = indicadoresOriginales[isVenta ? 'venta' : 'alquiler']?.find(orig =>
                                                        orig.tipoReforma.nombre === item.tipoReforma.nombre &&
                                                        orig.constructor === item.constructor
                                                    );
                                                    if (originalItem) {
                                                        costeOriginal = originalItem.costeReforma;
                                                        rentabilidadOriginal = isVenta ? originalItem.venta?.rentabilidadAnual : originalItem.alquiler?.rentabilidadAnual;
                                                        subValueOriginal = isVenta ? null : (originalItem.alquiler?.tiempoRetornoMeses ? `${originalItem.alquiler?.tiempoRetornoMeses} meses` : null);
                                                    }
                                                }
                                            }
                                            return (
                                                <div 
                                                    key={`cell-${scenarioName}-${constructor}-${rowIndex}-${colIndex}`} 
                                                    className={`text-center space-y-2 relative hover:bg-gray-50 transition-colors duration-200 rounded-lg p-2 cursor-pointer`}
                                                    onClick={() => handleShowDetails(item, scenarioName, constructor)}
                                                    style={{ order: colIndex + 1 }}
                                                >
                                                    <div>
                                                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Reforma</div>
                                                        {modoIA && costeOriginal && item.originalData ? (
                                                            <div className="text-sm font-bold text-gray-900">
                                                                <span className="line-through text-gray-400 mr-1">{formatCurrency(costeOriginal)}</span>
                                                                <span className="text-blue-800">{formatCurrency(costeActual)}</span>
                                                            </div>
                                                        ) : (
                                                            <div className="text-sm font-bold text-gray-900">{formatCurrency(costeActual)}</div>
                                                        )}
                                                    </div>
                                                    {item.costesExtra > 0 && (
                                                        <div>
                                                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Costes extra</div>
                                                            <div className="text-sm font-semibold text-gray-900">{formatCurrency(item.costesExtra)}</div>
                                                            <div className="text-xs text-gray-500">(mobiliario, electrodomésticos, etc.)</div>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Rentabilidad</div>
                                                        {modoIA && rentabilidadOriginal && item.originalData ? (
                                                            <div className="text-lg font-bold text-gray-900">
                                                                <span className="line-through text-gray-400 mr-1">{rentabilidadOriginal}%</span>
                                                                <span className="text-blue-800">{mainValue}%</span>
                                                            </div>
                                                        ) : (
                                                            <div className="text-lg font-bold text-gray-900">{mainValue}%</div>
                                                        )}
                                                    </div>
                                                    {subValue && (
                                                        <div>
                                                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Retorno</div>
                                                            {modoIA && subValueOriginal && item.originalData ? (
                                                                <div className="text-sm font-semibold text-green-600">
                                                                    <span className="line-through text-gray-400 mr-1">{subValueOriginal}</span>
                                                                    <span className="text-green-800">{subValue}</span>
                                                                </div>
                                                            ) : (
                                                                <div className="text-sm font-semibold text-green-600">{subValue}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {/* Show original price for venta/alquiler if modoIA and originalData exist */}
                                                    {item.venta && (modoIA && item.originalData && item.originalData.venta?.precioEstimado) ? (
                                                        <div>
                                                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Precio venta</div>
                                                            <div className="text-sm font-medium text-gray-900">{formatCurrency(item.originalData.venta.precioEstimado)}</div>
                                                        </div>
                                                    ) : item.venta && item.venta.precioEstimado ? (
                                                        <div>
                                                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Precio venta</div>
                                                            <div className="text-sm font-medium text-gray-900">{formatCurrency(item.venta.precioEstimado)}</div>
                                                        </div>
                                                    ) : null}
                                                    {item.alquiler && (modoIA && item.originalData && item.originalData.alquiler?.precioMensualEstimado) ? (
                                                        <div>
                                                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Precio alquiler</div>
                                                            <div className="text-sm font-medium text-gray-900">{formatCurrency(item.originalData.alquiler.precioMensualEstimado)}</div>
                                                        </div>
                                                    ) : item.alquiler && item.alquiler.precioMensualEstimado ? (
                                                        <div>
                                                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Precio alquiler</div>
                                                            <div className="text-sm font-medium text-gray-900">{formatCurrency(item.alquiler.precioMensualEstimado)}</div>
                                                        </div>
                                                    ) : null}
                                                    <div className="text-xs font-medium text-blue-600 hover:text-blue-800 mt-2">
                                                        Ver detalles
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }

        // Process and normalize all scenarios data
        const normalizedItems = processScenariosData(items);

        // Find the single best option across all scenarios and constructors
        const bestOverall = findBestOverall(normalizedItems, isVenta);

        // Use the helper to sort scenarios and constructors
        const { scenariosByName, allConstructors, sortedScenarioNames } = sortScenariosAndConstructors(normalizedItems);

        return (
            <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4"><span className={`inline-block w-2 h-2 rounded-full bg-${color}-500 mr-2`}></span>{title}</h3>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {/* Header */}
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                        <div className="grid gap-4" style={{ gridTemplateColumns: `200px repeat(${allConstructors.length}, 1fr)` }}>
                            <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Escenario</div>
                            {allConstructors.map(constructor => (
                                <div key={constructor} className="text-xs text-gray-500 uppercase tracking-wide font-medium text-center">
                                    {constructor}
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Rows */}
                    {sortedScenarioNames.map(scenarioName => {
                        const scenarioItems = scenariosByName[scenarioName];
                        
                        return (
                            <div key={scenarioName} className="border-b border-gray-100">
                                <div className="grid gap-4 px-6 py-4" style={{ gridTemplateColumns: `200px repeat(${allConstructors.length}, 1fr)` }}>
                                    {/* Scenario name */}
                                    <div className="flex items-center gap-2">
                                        <span className="text-2xl">{getScenarioIcon(scenarioName)}</span>
                                        <div className="font-semibold text-gray-900">{scenarioName}</div>
                                    </div>
                                    
                                    {/* Constructor columns */}
                                    {allConstructors.map(constructor => {
                                        const item = scenarioItems.find(si => (si.constructor || 'Sin constructor') === constructor);
                                        if (!item) {
                                            return <div key={constructor} className="text-center text-gray-400">-</div>;
                                        }
                                        
                                        const { hasIA, costeActual, mainValue, iaMainValue, subValue, iaSubValue } = getScenarioData(item, isVenta);
                                        
                                        // Get original values for comparison when in IA mode
                                        let costeOriginal, rentabilidadOriginal, subValueOriginal;
                                        
                                        if (modoIA && indicadoresOriginales) {
                                            // First check if original data is embedded in the current item
                                            if (item.originalData) {
                                                costeOriginal = item.originalData.costeReforma;
                                                rentabilidadOriginal = isVenta ? item.originalData.venta?.rentabilidadAnual : item.originalData.alquiler?.rentabilidadAnual;
                                                subValueOriginal = isVenta ? null : (item.originalData.alquiler?.tiempoRetornoMeses ? `${item.originalData.alquiler.tiempoRetornoMeses} meses` : null);
                                            } else {
                                                // Find the corresponding original item from indicadoresOriginales
                                                const originalItem = indicadoresOriginales[isVenta ? 'venta' : 'alquiler']?.find(orig => 
                                                    orig.tipoReforma.nombre === item.tipoReforma.nombre && 
                                                    orig.constructor === item.constructor
                                                );
                                                
                                                if (originalItem) {
                                                    costeOriginal = originalItem.costeReforma;
                                                    rentabilidadOriginal = isVenta ? originalItem.venta?.rentabilidadAnual : originalItem.alquiler?.rentabilidadAnual;
                                                    subValueOriginal = isVenta ? null : (originalItem.alquiler?.tiempoRetornoMeses ? `${originalItem.alquiler.tiempoRetornoMeses} meses` : null);
                                                }
                                            }
                                        }
                                        
                                        return (
                                            <div 
                                                key={constructor} 
                                                className={`text-center space-y-2 relative hover:bg-gray-50 transition-colors duration-200 rounded-lg p-2 cursor-pointer`}
                                                onClick={() => handleShowDetails(item, scenarioName, constructor)}
                                            >
                                                <div>
                                                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Reforma</div>
                                                    {modoIA && costeOriginal && item.originalData ? (
                                                        <div className="text-sm font-bold text-gray-900">
                                                            <span className="line-through text-gray-400 mr-1">{formatCurrency(costeOriginal)}</span>
                                                            <span className="text-blue-800">{formatCurrency(costeActual)}</span>
                                                        </div>
                                                    ) : (
                                                        <div className="text-sm font-bold text-gray-900">{formatCurrency(costeActual)}</div>
                                                    )}
                                                </div>
                                                
                                                {item.costesExtra > 0 && (
                                                    <div>
                                                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Costes extra</div>
                                                        <div className="text-sm font-semibold text-gray-900">{formatCurrency(item.costesExtra)}</div>
                                                        <div className="text-xs text-gray-500">(mobiliario, electrodomésticos, etc.)</div>
                                                    </div>
                                                )}
                                                
                                                <div>
                                                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Rentabilidad</div>
                                                    {modoIA && rentabilidadOriginal && item.originalData ? (
                                                        <div className="text-lg font-bold text-gray-900">
                                                            <span className="line-through text-gray-400 mr-1">{rentabilidadOriginal}%</span>
                                                            <span className="text-blue-800">{mainValue}%</span>
                                                        </div>
                                                    ) : (
                                                        <div className="text-lg font-bold text-gray-900">{mainValue}%</div>
                                                    )}
                                                </div>
                                                
                                                {subValue && (
                                                    <div>
                                                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Retorno</div>
                                                        {modoIA && subValueOriginal && item.originalData ? (
                                                            <div className="text-sm font-semibold text-green-600">
                                                                <span className="line-through text-gray-400 mr-1">{subValueOriginal}</span>
                                                                <span className="text-green-800">{subValue}</span>
                                                            </div>
                                                        ) : (
                                                            <div className="text-sm font-semibold text-green-600">{subValue}</div>
                                                        )}
                                                    </div>
                                                )}
                                                
                                                {/* Show original price for venta/alquiler if modoIA and originalData exist */}
                                                {item.venta && (modoIA && item.originalData && item.originalData.venta?.precioEstimado) ? (
                                                    <div>
                                                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Precio venta</div>
                                                        <div className="text-sm font-medium text-gray-900">{formatCurrency(item.originalData.venta.precioEstimado)}</div>
                                                    </div>
                                                ) : item.venta && item.venta.precioEstimado ? (
                                                    <div>
                                                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Precio venta</div>
                                                        <div className="text-sm font-medium text-gray-900">{formatCurrency(item.venta.precioEstimado)}</div>
                                                    </div>
                                                ) : null}
                                                {item.alquiler && (modoIA && item.originalData && item.originalData.alquiler?.precioMensualEstimado) ? (
                                                    <div>
                                                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Precio alquiler</div>
                                                        <div className="text-sm font-medium text-gray-900">{formatCurrency(item.originalData.alquiler.precioMensualEstimado)}</div>
                                                    </div>
                                                ) : item.alquiler && item.alquiler.precioMensualEstimado ? (
                                                    <div>
                                                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Precio alquiler</div>
                                                        <div className="text-sm font-medium text-gray-900">{formatCurrency(item.alquiler.precioMensualEstimado)}</div>
                                                    </div>
                                                ) : null}

                                                <div className="text-xs text-blue-600 font-semibold mt-2">
                                                    Ver detalles
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Existing content */}
            {renderGroup("Venta", indicadores?.venta, true, "blue")}
            {renderGroup("Alquiler", indicadores?.alquiler, false, "green")}
            
            {/* Vacation Rental Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900"><span className="inline-block w-2 h-2 rounded-full bg-purple-500 mr-2"></span>Alquiler Vacacional</h3>
                    {!loadingVacacional && !(vacacional && (Array.isArray(vacacional) ? vacacional.length > 0 : (vacacional.alquiler && vacacional.alquiler.length > 0))) && !vacacional?.noDataAvailable && (
                        <button
                            onClick={onCalcularVacacional}
                            disabled={loadingVacacional}
                            className="bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                        >
                            {loadingVacacional ? "Calculando..." : "Calcular"}
                        </button>
                    )}
                </div>
                
                {loadingVacacional ? (
                    <div className="text-center py-8">
                        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-gray-700 font-semibold">Calculando indicadores de alquiler vacacional...</p>
                    </div>
                ) : vacacional?.noDataAvailable ? (
                    <div className="text-center py-8">
                        <div className="text-6xl mb-4">🌴</div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">Datos no disponibles</h4>
                        <p className="text-gray-600 mb-4">
                            {vacacional.reason === 'NO_DATA_FOR_LOCATION' 
                                ? `En estos momentos no disponemos de datos suficientes sobre el mercado de Alquiler vacacional en ${vacacional.location.municipio}${vacacional.location.distrito ? ', ' + vacacional.location.distrito : ''}. Suscríbete para estar al tanto de cambios...`
                                : 'No hay datos disponibles para el análisis de alquiler vacacional.'
                            }
                        </p>
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                            <p className="text-sm text-gray-700">
                                <strong>¿Por qué no hay datos?</strong><br/>
                                El mercado de alquiler vacacional requiere datos específicos de precios por zona que aún no están disponibles para esta ubicación.
                            </p>
                        </div>
                    </div>
                ) : vacacional && (Array.isArray(vacacional) ? vacacional.length > 0 : (vacacional.alquiler && vacacional.alquiler.length > 0)) ? (
                    <div className="space-y-4">
                        {/* Vacation Rental Scenarios Table */}
                        {renderGroup("Alquiler Vacacional", Array.isArray(vacacional) ? vacacional : vacacional.alquiler, false, "purple")}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <div className="text-6xl mb-4">🌴</div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">Indicadores de Alquiler Vacacional</h4>
                        <p className="text-gray-600 mb-6">Los indicadores de alquiler vacacional aún no han sido calculados para esta propiedad.</p>
                        <button
                            onClick={onCalcularVacacional}
                            className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                        >
                            Calcular Indicadores Vacacionales
                        </button>
                        <p className="text-sm text-gray-500 mt-3">
                            Incluye análisis de rentabilidad, costes de mobiliario, electrodomésticos y licencias
                        </p>
                    </div>
                )}
            </div>

            {/* Scenario Details Modal */}
            <ScenarioDetailsModal
                isOpen={showDetailsModal}
                scenario={selectedScenario}
                onClose={() => setShowDetailsModal(false)}
                onShowCostesExtra={() => {
                    setShowCostesExtraModal(true);
                }}
                onCargarCostesVacacional={() => {
                    if (onCargarCostesVacacional) {
                        return onCargarCostesVacacional();
                    }
                }}
                costesVacacional={costesVacacional}
            />

            {/* Costes Extra Modal */}
            <CostesExtraModal
                isOpen={showCostesExtraModal}
                costesData={costesVacacional?.costes?.costes}
                onClose={() => setShowCostesExtraModal(false)}
            />
        </div>
    );
};

export default AdvancedInvestmentIndicators;